import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { forkJoin, throwError, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { NotaService } from '../../api/services/nota.service';
import { BolsaInstituicaoCursoService } from '../../api/services/bolsa-instituicao-curso.service';
import { PlanoCurricularService } from '../../api/services/plano-curricular.service';
import { PlanoCurricularDisciplinaService } from '../../api/services/plano-curricular-disciplina.service';
import { DisciplinaRow } from '../../api/models/nota.model';
import { Select } from 'primeng/select';

interface MatriculaOption {
  id: number;
  label: string;
  raw: any;
}

@Component({
  selector: 'app-lancar-notas',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ToastModule,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    TooltipModule,
    Select,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './lancar-notas.component.html',
  styleUrls: ['./lancar-notas.component.scss'],
})
export class LancarNotasComponent implements OnInit {
  @Input() bolsaId!: number;
  @Input() matriculaId!: number;
  @Input() anoCurricular!: string;
  @Input() anoLectivo!: string;
  @Input() bolseiroNome!: string;
  @Input() curso!: string;
  @Input() instituicao!: string;
  @Output() fechar = new EventEmitter<void>();

  private notaService = inject(NotaService);
  private bolsaInstituicaoCursoService = inject(BolsaInstituicaoCursoService);
  private planoCurricularService = inject(PlanoCurricularService);
  private planoCurricularDisciplinaService = inject(
    PlanoCurricularDisciplinaService,
  );
  messageService = inject(MessageService);
  confirmationService = inject(ConfirmationService);
  selectedMatriculaId: number | null = null;
  selectedMatricula?: MatriculaOption;

  isLoading = true;
  disciplinaRows: DisciplinaRow[] = [];
  @Input() matriculas: any[] = [];
  matriculaOptions: MatriculaOption[] = [];
  semPlanoAnoCurricular = false; // true quando fallback sem AnoCurricular
  planoCurricularID: number | null = null;

  get savedCount(): number {
    return this.disciplinaRows.filter((r) => r.saved).length;
  }

  get hasPendingChanges(): boolean {
    return this.disciplinaRows.some((r) => r.inputValue !== '' && !r.saved);
  }

  get matriculaActual(): any {
    return this.selectedMatricula?.raw;
  }

  get cursoActual(): string {
    return this.asText(this.matriculaActual?.curso, this.curso);
  }

  get instituicaoActual(): string {
    return this.asText(
      this.matriculaActual?.instituicaoEnsino,
      this.instituicao,
    );
  }

  get anoCurricularActual(): string {
    return String(
      this.matriculaActual?.anoCurricular ?? this.anoCurricular ?? '',
    );
  }

  get anoLectivoActual(): string {
    return this.asText(
      this.matriculaActual?.anoLectivo,
      this.anoLectivo,
      'ano',
    );
  }

  ngOnInit(): void {
    this.loadData();

    this.buildMatriculaOptions();

    if (this.matriculaOptions.length === 1) {
      this.selectedMatriculaId = this.matriculaOptions[0].id;
      this.onMatriculaChange(this.selectedMatriculaId);
    }
  }

  onMatriculaChange(matriculaId: number | null): void {
    this.selectedMatricula = this.matriculaOptions.find(
      (item) => item.id === Number(matriculaId),
    );

    if (!matriculaId) return;
    this.loadData(Number(matriculaId));
  }

  private buildMatriculaOptions(): void {
    const source = this.matriculas.length
      ? this.matriculas
      : this.matriculaId
        ? [
            {
              id: this.matriculaId,
              anoCurricular: this.anoCurricular,
              anoLectivo: this.anoLectivo,
              curso: this.curso,
              instituicaoEnsino: this.instituicao,
            },
          ]
        : [];

    this.matriculaOptions = source
      .filter((matricula) => Number(matricula?.id) > 0)
      .map((matricula) => ({
        id: Number(matricula.id),
        label: this.matriculaLabel(matricula),
        raw: matricula,
      }));
  }

  private matriculaLabel(matricula: any): string {
    const anoLectivo = this.asText(matricula?.anoLectivo, undefined, 'ano');
    const anoCurricular = matricula?.anoCurricular
      ? `${matricula.anoCurricular}.º ano`
      : '';
    const estado = this.asText(matricula?.estado);
    const detalhes = [anoLectivo, anoCurricular, estado]
      .filter(Boolean)
      .join(' • ');
    return detalhes
      ? `Matrícula #${matricula.id} — ${detalhes}`
      : `Matrícula #${matricula.id}`;
  }

  loadData(matriculaId?: number): void {
    this.isLoading = true;
    this.disciplinaRows = [];
    const matriculaIdParaCarregar = this.selectedMatriculaId || matriculaId;

    // 1. BolsaInstituicaoCurso → instituicaoEnsinoCursoID
    this.bolsaInstituicaoCursoService
      .list({
        BolsaID: this.bolsaId,
        PageNumber: 1,
        PageSize: 5,
        matriculaId: matriculaIdParaCarregar,
      })
      .pipe(
        switchMap((bicRes: any) => {
          const bic = bicRes?.data?.[0];
          const instituicaoEnsinoCursoID = bic?.instituicaoEnsinoCursoID;
          if (!instituicaoEnsinoCursoID) {
            return throwError(
              () =>
                new Error('Plano curricular não encontrado para esta bolsa.'),
            );
          }
          // 2. PlanoCurricular pelo InstituicaoDeEnsinoCursoID
          return this.planoCurricularService.list({
            InstituicaoDeEnsinoCursoID: instituicaoEnsinoCursoID,
            PageNumber: 1,
            PageSize: 5,
          });
        }),
        switchMap((pcRes: any) => {
          const id = pcRes?.data?.[0]?.id;
          if (!id) {
            return throwError(
              () =>
                new Error(
                  'Plano curricular não configurado para esta instituição/curso.',
                ),
            );
          }
          this.planoCurricularID = id;
          // 3. Disciplinas filtradas por AnoCurricular + notas em paralelo
          return forkJoin({
            disciplinas: this.planoCurricularDisciplinaService.list({
              PlanoCurricularID: id,
              AnoCurricular: Number(this.anoCurricular) || undefined,
              PageNumber: 1,
              PageSize: 100,
            }),
            notas: this.notaService.list({
              MatriculaId: this.matriculaId,
              PageNumber: 1,
              PageSize: 100,
            }),
          });
        }),
        // 4. Se filtro de ano devolveu 0 resultados, repete sem AnoCurricular
        switchMap(({ disciplinas, notas }: any) => {
          if (
            (disciplinas?.data ?? []).length === 0 &&
            this.anoCurricular &&
            this.planoCurricularID
          ) {
            this.semPlanoAnoCurricular = true;
            return forkJoin({
              disciplinas: this.planoCurricularDisciplinaService.list({
                PlanoCurricularID: this.planoCurricularID,
                PageNumber: 1,
                PageSize: 100,
              }),
              notas: of(notas),
            });
          }
          return of({ disciplinas, notas });
        }),
      )
      .subscribe({
        next: ({ disciplinas, notas }: any) => {
          const notasExistentes: any[] = notas?.data ?? [];
          this.disciplinaRows = (disciplinas?.data ?? []).map((d: any) => {
            const notaExistente = notasExistentes.find(
              (n: any) => Number(n.disciplinaId) === Number(d.disciplinaID),
            );
            return {
              disciplinaId: Number(d.disciplinaID),
              nome: d.disciplina,
              notaActual: notaExistente?.nota ?? null,
              notaId: notaExistente?.id ?? null,
              inputValue: notaExistente?.nota ?? '',
              saved: notaExistente != null,
              loading: false,
              error: false,
            };
          });
          this.isLoading = false;
        },
        error: (err: any) => {
          this.isLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Erro ao carregar',
            detail: err?.message || 'Não foi possível carregar as disciplinas.',
          });
        },
      });
  }

  /*
  saveNota(row: DisciplinaRow): void {
    const valor = Number(row.inputValue);
    if (row.inputValue === '' || isNaN(valor) || valor < 0 || valor > 20) {
      this.markError(row);
      this.messageService.add({
        severity: 'warn',
        summary: 'Nota inválida',
        detail: 'Introduz um valor numérico entre 0 e 20.',
      });
      return;
    }

    row.loading = true;
    const req$ =
      row.notaId !== null
        ? this.notaService.update({ id: row.notaId, nota: String(valor) })
        : this.notaService.create({
            matriculaId: this.matriculaId,
            disciplinaId: row.disciplinaId,
            nota: String(valor),
          });

    req$.subscribe({
      next: (res: any) => {
        row.loading = false;
        const ok =
          res?.retorno?.codigo === 200 ||
          res?.data != null ||
          res?.success === true ||
          res?.id != null;
        if (ok) {
          row.notaActual = String(valor);
          row.saved = true;
          if (!row.notaId) {
            row.notaId = res?.data?.id ?? res?.id ?? null;
          }
          this.messageService.add({
            severity: 'success',
            summary: 'Guardado',
            detail: `Nota de ${row.nome} guardada com sucesso.`,
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: `Erro ${res?.retorno?.codigo ?? ''}`.trim(),
            detail: res?.retorno?.mensagem || 'Erro ao guardar a nota.',
          });
        }
      },
      error: (err: any) => {
        row.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: err?.message || 'Erro ao guardar a nota.',
        });
      },
    });
  }

  saveAll(): void {
    const pendentes = this.disciplinaRows.filter(
      (r) => r.inputValue !== '' && !r.saved,
    );
    if (pendentes.length === 0) {
      this.messageService.add({
        severity: 'info',
        summary: 'Sem alterações',
        detail: 'Não há notas pendentes por guardar.',
      });
      return;
    }
    pendentes.forEach((row) => this.saveNota(row));
  }
  */

  tentarFechar(): void {
    /*
    if (this.hasPendingChanges) {
      this.confirmationService.confirm({
        message:
          'Tem notas preenchidas por guardar. Deseja fechar mesmo assim?',
        header: 'Alterações não guardadas',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Fechar',
        rejectLabel: 'Cancelar',
        accept: () => this.fechar.emit(),
      });
    } else {
      this.fechar.emit();
    }
    */
    this.fechar.emit();
  }

  /*
  onInputChange(row: DisciplinaRow): void {
    if (row.saved && row.inputValue !== row.notaActual) {
      row.saved = false;
    }
    if (row.inputValue !== '') {
      const val = Number(row.inputValue);
      if (!isNaN(val)) {
        if (val > 20) {
          this.markError(row);
          // pequeno delay para o utilizador ver o erro antes de corrigir
          setTimeout(() => {
            row.inputValue = '20';
          }, 600);
        } else if (val < 0) {
          row.inputValue = '0';
        }
      }
    }
  }
  */

  formatAnoCurricular(ano: string | null | undefined): string {
    if (!ano) return '—';
    return `${ano}.º ano`;
  }

  private asText(value: any, fallback?: string, property = 'nome'): string {
    if (typeof value === 'string' || typeof value === 'number')
      return String(value);
    return String(value?.[property] ?? value?.designacao ?? fallback ?? '');
  }

  private markError(row: DisciplinaRow): void {
    row.error = true;
    setTimeout(() => {
      row.error = false;
    }, 1200);
  }
}
